import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  FormHelperText,
  InputAdornment,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import { Edit, Delete, Add, AttachMoney, Percent } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../api';

// Tipos de condiciones negociadas
const TIPO_CONDICIONES = [
  { value: 'ESTADIA', label: 'ESTADIA' },
  { value: 'DEVOLUCION', label: 'DEVOLUCION' },
  { value: 'REPARTO', label: 'REPARTO' },
  { value: 'RECOLECCION', label: 'RECOLECCION' }
  
];

// Conceptos base para cálculo de porcentajes
const CONCEPTOS_BASE = [
  { value: 'FLETE', label: 'FLETE' }
  //{ value: 'COSTO_TOTAL', label: 'COSTO TOTAL' },
];

const CondicionesNegociadasManagement = ({ selectedLine }) => {
  // Estados para la gestión de condiciones
  const [condiciones, setCondiciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [condicionToDelete, setCondicionToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Estado para el formulario
  const [formData, setFormData] = useState({
    tipo: '',
    monto: '',
    moneda: 'MXN',
    descripcion: '',
    limiteDias: 1,
    linea_transporte_id: selectedLine?.id || 0,
    tipoCalculo: 'FIJO',  // FIJO o PORCENTAJE
    porcentaje: '',
    conceptoBase: ''
  });
  
  // Estado para errores de validación
  const [errors, setErrors] = useState({
    tipo: false,
    monto: false,
    porcentaje: false,
    conceptoBase: false
  });

  // Cargar condiciones cuando se selecciona una línea
  useEffect(() => {
    if (selectedLine) {
      fetchCondiciones();
      setFormData(prev => ({
        ...prev,
        linea_transporte_id: selectedLine.id
      }));
    }
  }, [selectedLine]);

  // Función para cargar las condiciones desde el backend
  const fetchCondiciones = async () => {
    if (!selectedLine) return;
    
    setLoading(true);
    try {
      // Llamada a la API para obtener condiciones negociadas de la línea seleccionada
      const response = await api.get(`/api/v1/lineas-transporte/${selectedLine.id}/condiciones-negociadas`);
      
      // Mapear la respuesta a nuestro formato interno
      const condicionesFormateadas = response.data.map(condicion => ({
        id: condicion.id,
        tipo: condicion.tipo,
        tipoCalculo: condicion.tipo_calculo,
        monto: condicion.monto,
        moneda: condicion.moneda || 'MXN',
        porcentaje: condicion.porcentaje,
        conceptoBase: condicion.concepto_base,
        descripcion: condicion.descripcion || '',
        limiteDias: condicion.limite_dias,
        linea_transporte_id: condicion.linea_transporte_id,
        created_at: condicion.created_at,
        updated_at: condicion.updated_at
      }));
      
      setCondiciones(condicionesFormateadas);
      toast.success('Condiciones cargadas correctamente');
    } catch (error) {
      console.error('Error al cargar condiciones:', error);
      toast.error('Error al cargar las condiciones negociadas');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar errores al modificar el campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  // Abrir formulario para crear o editar
  const handleOpenForm = (condicion = null) => {
    if (condicion) {
      setFormData({
        tipo: condicion.tipo,
        monto: condicion.tipoCalculo === 'FIJO' ? condicion.monto : '',
        moneda: condicion.moneda || 'MXN',
        descripcion: condicion.descripcion,
        limiteDias: condicion.limiteDias,
        linea_transporte_id: selectedLine.id,
        id: condicion.id,
        tipoCalculo: condicion.tipoCalculo || 'FIJO',
        porcentaje: condicion.tipoCalculo === 'PORCENTAJE' ? condicion.porcentaje : '',
        conceptoBase: condicion.conceptoBase || ''
      });
      setIsEditing(true);
    } else {
      setFormData({
        tipo: '',
        monto: '',
        moneda: 'MXN',
        descripcion: '',
        limiteDias: 1,
        linea_transporte_id: selectedLine.id,
        tipoCalculo: 'FIJO',
        porcentaje: '',
        conceptoBase: ''
      });
      setIsEditing(false);
    }
    
    setErrors({
      tipo: false,
      monto: false,
      porcentaje: false,
      conceptoBase: false
    });
    
    setOpenDialog(true);
  };

  // Cerrar formulario
  const handleCloseForm = () => {
    setOpenDialog(false);
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {
      tipo: !formData.tipo,
      monto: formData.tipoCalculo === 'FIJO' ? (!formData.monto || isNaN(formData.monto) || formData.monto <= 0) : false,
      porcentaje: formData.tipoCalculo === 'PORCENTAJE' ? (!formData.porcentaje || isNaN(formData.porcentaje) || formData.porcentaje <= 0) : false,
      conceptoBase: formData.tipoCalculo === 'PORCENTAJE' ? !formData.conceptoBase : false
    };
    
    setErrors(newErrors);
    
    return !Object.values(newErrors).some(error => error);
  };

  // Guardar condición
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Por favor complete los campos requeridos correctamente');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Preparar los datos según la API
      const condicionData = {
        tipo: formData.tipo,
        tipo_calculo: formData.tipoCalculo,
        monto: formData.tipoCalculo === 'FIJO' ? parseFloat(formData.monto) : null,
        moneda: formData.tipoCalculo === 'FIJO' ? formData.moneda : null,
        porcentaje: formData.tipoCalculo === 'PORCENTAJE' ? parseFloat(formData.porcentaje) : null,
        concepto_base: formData.tipoCalculo === 'PORCENTAJE' ? formData.conceptoBase : null,
        descripcion: formData.descripcion || null,
        limite_dias: parseInt(formData.limiteDias),
        linea_transporte_id: formData.linea_transporte_id
      };
      
      if (isEditing) {
        // Actualizar condición existente
        await api.put(`/api/v1/condiciones-negociadas/${formData.id}`, condicionData);
        toast.success('Condición actualizada correctamente');
      } else {
        // Crear nueva condición
        await api.post('/api/v1/condiciones-negociadas/', condicionData);
        toast.success('Condición creada correctamente');
      }
      
      // Recargar las condiciones
      fetchCondiciones();
      handleCloseForm();
    } catch (error) {
      console.error('Error:', error);
      toast.error(isEditing ? 'Error al actualizar la condición' : 'Error al crear la condición');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmar eliminación
  const handleConfirmDelete = (condicion) => {
    setCondicionToDelete(condicion);
    setDeleteConfirmOpen(true);
  };

  // Eliminar condición
  const handleDelete = async () => {
    if (!condicionToDelete) return;
    
    try {
      // Eliminar condición de la API
      await api.delete(`/api/v1/condiciones-negociadas/${condicionToDelete.id}`);
      
      // Actualizar estado local
      setCondiciones(prev => prev.filter(item => item.id !== condicionToDelete.id));
      
      toast.success('Condición eliminada correctamente');
      setDeleteConfirmOpen(false);
      setCondicionToDelete(null);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar la condición');
    }
  };

  // Mostrar mensaje cuando no hay línea seleccionada
  if (!selectedLine) {
    return (
      <Alert severity="info">
        Por favor seleccione una línea de transporte para administrar sus condiciones negociadas
      </Alert>
    );
  }

  // Renderizar componente principal
  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Condiciones Negociadas con {selectedLine.name}</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenForm()}
            disabled={selectedLine?.blocked}
          >
            Nueva Condición
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : condiciones.length === 0 ? (
          <Alert severity="info">
            No hay condiciones negociadas registradas para esta línea de transporte
          </Alert>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Límite (Días)</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {condiciones.map((condicion) => (
                  <TableRow key={condicion.id}>
                    <TableCell>
                      {TIPO_CONDICIONES.find(t => t.value === condicion.tipo)?.label || condicion.tipo}
                    </TableCell>
                    <TableCell>
                      {condicion.tipoCalculo === 'FIJO' ? (
                        <Tooltip title="Monto Fijo">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AttachMoney fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                            <span>Monto Fijo</span>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title={`Porcentaje sobre ${CONCEPTOS_BASE.find(c => c.value === condicion.conceptoBase)?.label || condicion.conceptoBase}`}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Percent fontSize="small" sx={{ mr: 0.5, color: 'secondary.main' }} />
                            <span>{CONCEPTOS_BASE.find(c => c.value === condicion.conceptoBase)?.label || condicion.conceptoBase}</span>
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {condicion.tipoCalculo === 'FIJO' ? (
                        <>
                          {condicion.monto?.toLocaleString('es-MX', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) || '0.00'} {condicion.moneda}
                        </>
                      ) : (
                        <>
                          {condicion.porcentaje}%
                        </>
                      )}
                    </TableCell>
                    <TableCell>{condicion.descripcion}</TableCell>
                    <TableCell>{condicion.limiteDias}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenForm(condicion)}
                        disabled={selectedLine?.blocked}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleConfirmDelete(condicion)}
                        disabled={selectedLine?.blocked}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Formulario Modal */}
      <Dialog open={openDialog} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Editar Condición Negociada' : 'Nueva Condición Negociada'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={errors.tipo}>
                <InputLabel>Tipo de Condición</InputLabel>
                <Select
                  name="tipo"
                  value={formData.tipo}
                  label="Tipo de Condición"
                  onChange={handleInputChange}
                >
                  {TIPO_CONDICIONES.map(tipo => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tipo && <FormHelperText>El tipo es obligatorio</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                  Tipo de Cálculo
                </Typography>
                <RadioGroup
                  row
                  name="tipoCalculo"
                  value={formData.tipoCalculo}
                  onChange={handleInputChange}
                >
                  <FormControlLabel 
                    value="FIJO" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoney sx={{ mr: 0.5 }} />
                        <span>Monto Fijo</span>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="PORCENTAJE" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Percent sx={{ mr: 0.5 }} />
                        <span>Porcentaje</span>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            {formData.tipoCalculo === 'FIJO' ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="monto"
                    label="Monto"
                    value={formData.monto}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    error={errors.monto}
                    helperText={errors.monto ? 'Ingrese un monto válido' : ''}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      name="moneda"
                      value={formData.moneda}
                      label="Moneda"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="MXN">MXN</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="porcentaje"
                    label="Porcentaje"
                    value={formData.porcentaje}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    error={errors.porcentaje}
                    helperText={errors.porcentaje ? 'Ingrese un porcentaje válido' : ''}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={errors.conceptoBase}>
                    <InputLabel>Concepto Base</InputLabel>
                    <Select
                      name="conceptoBase"
                      value={formData.conceptoBase}
                      label="Concepto Base"
                      onChange={handleInputChange}
                    >
                      {CONCEPTOS_BASE.map(concepto => (
                        <MenuItem key={concepto.value} value={concepto.value}>
                          {concepto.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.conceptoBase && <FormHelperText>Seleccione un concepto base</FormHelperText>}
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={formData.descripcion}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Detalles sobre la condición negociada"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                name="limiteDias"
                label="Límite de Días"
                value={formData.limiteDias}
                onChange={handleInputChange}
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                helperText="Días aplicables antes de que se cobre la condición (0 = No aplica)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? 'Procesando...' : (isEditing ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de Eliminación */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar esta condición? Esta acción no se puede deshacer.
          </Typography>
          {condicionToDelete && (
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
              {TIPO_CONDICIONES.find(t => t.value === condicionToDelete.tipo)?.label}: 
              {condicionToDelete.tipoCalculo === 'FIJO' 
                ? ` ${condicionToDelete.monto} ${condicionToDelete.moneda}`
                : ` ${condicionToDelete.porcentaje}% sobre ${CONCEPTOS_BASE.find(c => c.value === condicionToDelete.conceptoBase)?.label || condicionToDelete.conceptoBase}`
              }
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CondicionesNegociadasManagement;